/**
 * EnrollmentsTable — a single project's annotator roster (mockup
 * `#s-adetail` "Enrollments" card): local `<table>`, columns Annotator /
 * State (`StatusBadge`) / Phase / Last agreement / action.
 *
 * Pending rows ("pending_approval") get a "Review & approve" action that
 * opens a `Dialog` showing `ScorecardView` for that batch's scorecard,
 * then an in-dialog "Approve" button that actually fires
 * `useApproveEnrollmentMutation`. On a 409 (pool exhausted) the dialog
 * shows an inline pool-empty message instead of closing — the admin can
 * still see the scorecard, they just can't advance the annotator until the
 * pool is restocked.
 *
 * Per-row pool-exhausted tracking uses the same `Set<string>` keyed pattern
 * as `AdminControlRoom` (keyed by annotator email — unique within a single
 * project's enrollments): approving one row's 409 must never clear a
 * different row's still-exhausted flag, and a single nullable string
 * previously let exactly that happen there. See AdminControlRoom.tsx's own
 * comment on `poolExhaustedKeys` for the full history.
 *
 * The "Last agreement" column reads `Enrollment.scorecard`, backfilled
 * server-side with the most-recently finalized batch's scorecard. A missing
 * one renders "—", never a fabricated value — and neither does a
 * non-`ok` status, whose `overall_agreement: 0` is a placeholder rather
 * than a real score (see `agreementOf`).
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/design-system/components/react";
import {
  useApproveEnrollmentMutation,
  useAgreementCheckMutation,
} from "../../../../hooks/useFactoryQueries";
import { useProgressQuery } from "../../../../hooks/useLabelingQueries";
import type { Enrollment, EnrollmentState } from "../../../../types/factory";
import {
  batchSeq,
  factoryComparisonMetric,
  factoryComparisonScore,
} from "../../../../utils/factoryDisplay";
import { logger } from "../../../../utils/logger";
import { FaChevronRight } from "../../../../utils/icons";
import { ProgressMeter, StatusBadge, type StatusTone } from "../primitives";
import { ScorecardView } from "./ScorecardView";

const STATE_TONE: Record<EnrollmentState, StatusTone> = {
  active: "sig",
  pending_approval: "sig",
  blocked_pool_empty: "warn",
  completed: "ghost",
};

const STATE_LABEL: Record<EnrollmentState, string> = {
  active: "active",
  pending_approval: "pending approval",
  blocked_pool_empty: "blocked · pool empty",
  completed: "completed",
};

function readScorecard(enrollment: Enrollment): Record<string, unknown> | null {
  return enrollment.scorecard ?? null;
}

/**
 * The scorecard's agreement, or null when there isn't a real one.
 *
 * Gate on `status` first. The backend emits `overall_agreement: 0` as a
 * PLACEHOLDER in two non-scored states — `no_ground_truth` (project has no
 * `gt_annotator`) and `pending_peer` (no counterpart finished the overlap
 * slice) — see factory_eval.py. Accepting any finite number rendered those
 * as a red 0.00, i.e. a fabricated zero-agreement score for an annotator who
 * simply has no peer overlap yet, on the table used to decide phase
 * advancement. `ScorecardView` already refuses to render a number for both
 * statuses; this keeps the two surfaces telling the same story.
 */
function agreementOf(scorecard: Record<string, unknown> | null): number | null {
  return factoryComparisonScore(scorecard);
}

function AgreementCell({
  scorecard,
  isChecking,
  errored,
}: {
  scorecard: Record<string, unknown> | null;
  isChecking: boolean;
  errored?: boolean;
}) {
  if (isChecking) {
    return <span className="text-xs text-fg-faint">…</span>;
  }
  if (errored) {
    return <span className="text-[11px] text-warn">check failed</span>;
  }
  const agreement = agreementOf(scorecard);
  if (agreement === null) {
    return <span className="text-sm text-fg-faint">—</span>;
  }
  const low = agreement < 0.8;
  return (
    <span className="flex flex-col">
      <span className={`font-mono text-[11.5px] ${low ? "text-warn" : "text-fg-muted"}`}>
        {agreement.toFixed(2)}
      </span>
      <span className="text-[10px] text-fg-faint">{factoryComparisonMetric(scorecard)}</span>
    </span>
  );
}

function handle(annotator: string): string {
  const atIdx = annotator.indexOf("@");
  return atIdx > 0 ? annotator.slice(0, atIdx) : annotator;
}

/**
 * Exact three-phase label. Phase 2 is terminal.
 */
function phaseLabel(phase: number): string {
  if (phase === 0) return "Demo";
  return `Phase ${phase}`;
}

const WORKLIST_LABEL_RE = /-phase(\d+)-batch(\d+)$/i;
const FACTORY_WORKLIST_LABEL_RE = /-p([0-2])-.+-(\d{3})$/i;

/** Human label for a granted worklist id, e.g. `us3d-phase01-batch29` →
 *  "Phase 1 · Batch 29". Falls back to the id with the project prefix stripped. */
function worklistLabel(id: string, projectId: string): string {
  const m = id.match(WORKLIST_LABEL_RE);
  if (m) return `Phase ${parseInt(m[1], 10)} · Batch ${parseInt(m[2], 10)}`;
  const factory = id.match(FACTORY_WORKLIST_LABEL_RE);
  if (factory) {
    const phase = parseInt(factory[1], 10);
    return `${phase === 0 ? "Demo" : `Phase ${phase}`} · Batch ${parseInt(factory[2], 10)}`;
  }
  if (id === `${projectId}-gt-shared`) return "Shared demo GT";
  return id.startsWith(`${projectId}-`) ? id.slice(projectId.length + 1) : id;
}

/** One granted worklist for an annotator: readable label + its own done/total
 *  progress bar (via /progress) + click-to-open. This is how migrated us3d
 *  enrollments — assigned via grants, not a factory current_batch — surface
 *  their real work. "—" when the worklist has no cases (never a fake 0%). */
function AssignedWorklistRow({
  worklistId,
  label,
  onOpen,
}: {
  worklistId: string;
  label: string;
  onOpen: (id: string) => void;
}) {
  const { data, isLoading } = useProgressQuery(worklistId);
  const total = data?.total_labels ?? 0;
  const done = Math.min(data?.completed_labels ?? 0, total);
  return (
    <button
      type="button"
      onClick={() => onOpen(worklistId)}
      title={`Open ${worklistId}`}
      data-testid={`worklist-${worklistId}`}
      className="flex w-full max-w-[260px] items-center gap-2 text-left transition hover:opacity-80"
    >
      <span className="shrink-0 font-mono text-xs text-signal-text-tint">{label}</span>
      <span className="min-w-0 flex-1">
        {isLoading ? (
          <span className="text-[10px] text-fg-faint">…</span>
        ) : total > 0 ? (
          <ProgressMeter done={done} total={total} display="percent" />
        ) : (
          <span className="text-[10px] text-fg-faint">—</span>
        )}
      </span>
      <FaChevronRight className="h-2.5 w-2.5 shrink-0 text-fg-muted" />
    </button>
  );
}

export interface EnrollmentsTableProps {
  projectId: string;
  enrollments: Enrollment[];
  /** annotator email → their granted worklist ids for this project (e.g.
   *  us3d-phaseNN-batchNN). Surfaced inline per row since migrated us3d
   *  enrollments carry no factory current_batch. */
  grantedWorklists?: Record<string, string[]>;
}

export function EnrollmentsTable({
  projectId,
  enrollments,
  grantedWorklists,
}: EnrollmentsTableProps) {
  const navigate = useNavigate();
  const openWorklist = (id: string) => navigate(`/labeling?project=${encodeURIComponent(id)}`);
  const approveMutation = useApproveEnrollmentMutation();
  const [poolExhausted, setPoolExhausted] = useState<Set<string>>(() => new Set());
  const [reviewing, setReviewing] = useState<Enrollment | null>(null);

  const agreementCheck = useAgreementCheckMutation();
  const [liveScorecards, setLiveScorecards] = useState<Map<string, Record<string, unknown>>>(
    () => new Map(),
  );
  const [checking, setChecking] = useState<Set<string>>(() => new Set());
  const [checkError, setCheckError] = useState<Set<string>>(() => new Set());

  const handleRunAgreementCheck = () => {
    const targets = enrollments
      .map((enrollment) => {
        const batch = [...(enrollment.batches ?? [])]
          .reverse()
          .find((candidate) => candidate.finalized && candidate.phase < 2);
        return { enrollment, batchId: batch?.batch_id ?? enrollment.current_batch_id };
      })
      .filter((target) => !!target.batchId);
    if (targets.length === 0) {
      // Migrated grant-based rosters have no factory batch, so there is
      // nothing to recompute. Say so — a click that changes nothing and
      // logs nothing is indistinguishable from a broken button.
      logger.warn(
        "[EnrollmentsTable] agreement check skipped: no enrollment in this project has a current batch",
      );
      return;
    }
    setChecking(new Set(targets.map(({ enrollment }) => enrollment.annotator)));
    setCheckError(new Set());
    const clearChecking = (annotator: string) =>
      setChecking((prev) => {
        const next = new Set(prev);
        next.delete(annotator);
        return next;
      });
    // `mutateAsync`, NOT `mutate`. `agreementCheck` is a single
    // `useMutation` instance, and `mutate` routes every call through that one
    // observer: each call overwrites `#mutateOptions` and calls
    // `removeObserver` on the previous mutation (query-core 5.101
    // mutationObserver.js), so for N rows only the LAST call's callbacks ever
    // fire. Rows 1..N-1 never cleared `checking` and spun forever behind a
    // permanently disabled button, while the backend still did N recomputes.
    // `mutateAsync` returns that call's own promise, unaffected by the
    // observer swap. Clearing in `finally` also closes the "row settles but
    // state leaks" gap structurally, rather than relying on both handlers
    // remembering to call it.
    targets.forEach(({ enrollment: e, batchId }) => {
      agreementCheck
        .mutateAsync({ pid: projectId, annotator: e.annotator, batchId: batchId! })
        .then((scorecard) => {
          setLiveScorecards((prev) => {
            const next = new Map(prev);
            next.set(e.annotator, scorecard);
            return next;
          });
          setCheckError((prev) => {
            if (!prev.has(e.annotator)) return prev;
            const next = new Set(prev);
            next.delete(e.annotator);
            return next;
          });
        })
        .catch((err: unknown) => {
          logger.error("[EnrollmentsTable] agreement check failed", err);
          setCheckError((prev) => {
            const next = new Set(prev);
            next.add(e.annotator);
            return next;
          });
          setLiveScorecards((prev) => {
            if (!prev.has(e.annotator)) return prev;
            const next = new Map(prev);
            next.delete(e.annotator);
            return next;
          });
        })
        .finally(() => clearChecking(e.annotator));
    });
  };

  const activeCount = enrollments.filter((e) => e.state === "active").length;
  const pendingCount = enrollments.filter((e) => e.state === "pending_approval").length;

  const handleApprove = (enrollment: Enrollment) => {
    const key = enrollment.annotator;
    setPoolExhausted((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    approveMutation.mutate(
      { pid: projectId, annotator: enrollment.annotator },
      {
        onSuccess: () => setReviewing(null),
        onError: (err: unknown) => {
          if ((err as { poolExhausted?: true })?.poolExhausted) {
            setPoolExhausted((prev) => {
              const next = new Set(prev);
              next.add(key);
              return next;
            });
            return;
          }
          logger.error("[EnrollmentsTable] approve failed", err);
        },
      },
    );
  };

  return (
    <div className="rounded border border-border/50 bg-surface">
      <div className="flex items-center gap-3 border-b border-border/50 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-fg">Enrollments</h3>
        {activeCount > 0 && <StatusBadge tone="sig">{activeCount} active</StatusBadge>}
        {pendingCount > 0 && <StatusBadge tone="sig">{pendingCount} pending</StatusBadge>}
        <button
          type="button"
          onClick={handleRunAgreementCheck}
          disabled={checking.size > 0}
          className="ml-auto rounded bg-primary/15 px-3 py-1.5 text-xs font-semibold text-signal-text-tint transition hover:bg-primary/25 disabled:opacity-50"
        >
          {checking.size > 0 ? "Checking…" : "Run agreement check"}
        </button>
      </div>

      {enrollments.length === 0 ? (
        <div className="p-8 text-center text-sm text-fg-muted">No enrollments yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-border/50 text-[11px] uppercase tracking-wide text-fg-dim">
                <th className="w-2/5 px-5 py-2.5 font-medium">Annotator</th>
                <th className="px-5 py-2.5 font-medium">State</th>
                <th className="px-5 py-2.5 font-medium">Phase</th>
                <th className="px-5 py-2.5 font-medium">Auto score</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {enrollments.map((enrollment) => {
                const exhausted = poolExhausted.has(enrollment.annotator);
                const worklists = grantedWorklists?.[enrollment.annotator] ?? [];
                return (
                  <tr
                    key={enrollment.annotator}
                    className="border-b border-border/50 last:border-b-0"
                  >
                    <td className="px-5 py-3.5 align-top">
                      <div className="text-sm text-fg">{handle(enrollment.annotator)}</div>
                      {/* Factory current batch, if this enrollment uses the batch flow. */}
                      {enrollment.current_batch_id &&
                        (enrollment.worklist_project_id ? (
                          <button
                            type="button"
                            onClick={() => openWorklist(enrollment.worklist_project_id!)}
                            title="Open this batch's worklist"
                            className="mt-0.5 inline-flex items-center gap-1 font-mono text-xs text-signal-text-tint transition hover:underline"
                          >
                            {phaseLabel(enrollment.phase)} batch{" "}
                            {batchSeq(enrollment.current_batch_id)}
                            <FaChevronRight className="h-2.5 w-2.5" />
                          </button>
                        ) : (
                          <div className="font-mono text-xs text-fg-muted">
                            {phaseLabel(enrollment.phase)} batch{" "}
                            {batchSeq(enrollment.current_batch_id)}
                          </div>
                        ))}
                      {/* Granted worklists (migrated us3d assigns via grants, not a
                          factory batch): each with its own progress + open link. */}
                      {worklists.length > 0 && (
                        <div className="mt-1.5 flex flex-col gap-1">
                          {worklists.map((w) => (
                            <AssignedWorklistRow
                              key={w}
                              worklistId={w}
                              label={worklistLabel(w, projectId)}
                              onOpen={openWorklist}
                            />
                          ))}
                        </div>
                      )}
                      {!enrollment.current_batch_id && worklists.length === 0 && (
                        <div className="text-xs text-fg-faint">no active batch</div>
                      )}
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <StatusBadge tone={STATE_TONE[enrollment.state]}>
                        {STATE_LABEL[enrollment.state]}
                      </StatusBadge>
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <span className="font-mono text-xs text-fg-muted">
                        {phaseLabel(enrollment.phase)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <AgreementCell
                        scorecard={
                          liveScorecards.get(enrollment.annotator) ?? readScorecard(enrollment)
                        }
                        isChecking={checking.has(enrollment.annotator)}
                        errored={checkError.has(enrollment.annotator)}
                      />
                    </td>
                    <td className="px-5 py-3.5 text-right align-top">
                      {enrollment.state === "pending_approval" ? (
                        <div className="flex flex-col items-end gap-1.5">
                          {exhausted && (
                            <span className="text-[11px] text-warn">
                              Pool empty — nothing to advance into
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setReviewing(enrollment)}
                            className="rounded bg-primary/15 px-3 py-1.5 text-xs font-semibold text-signal-text-tint transition hover:bg-primary/25"
                          >
                            Review &amp; approve
                          </button>
                        </div>
                      ) : enrollment.state === "active" ? (
                        <span className="text-[11px] text-fg-faint">auto</span>
                      ) : enrollment.state === "completed" ? (
                        <span className="text-[11px] text-fg-faint">completed</span>
                      ) : (
                        <span className="text-[11px] text-warn">needs cases</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={reviewing !== null} onOpenChange={(open) => !open && setReviewing(null)}>
        <DialogContent className="max-w-xl bg-surface border-border-strong">
          <DialogHeader>
            <DialogTitle className="text-fg">
              {reviewing ? `Review ${reviewing.annotator}` : "Review"}
            </DialogTitle>
          </DialogHeader>

          {reviewing && (
            <>
              <ScorecardView
                scorecard={liveScorecards.get(reviewing.annotator) ?? readScorecard(reviewing)}
              />
              {poolExhausted.has(reviewing.annotator) && (
                <div className="rounded border border-warn/30 bg-warn-tint px-3 py-2 text-xs text-warn">
                  Pool empty — nothing to advance {handle(reviewing.annotator)} into. Ingest more
                  cases, then retry.
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => reviewing && handleApprove(reviewing)}
              disabled={!reviewing}
              className="rounded bg-primary/15 px-3 py-1.5 text-xs font-semibold text-signal-text-tint transition hover:bg-primary/25 disabled:opacity-50"
            >
              Approve
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EnrollmentsTable;
